import { Link } from 'react-router-dom';
import { useSound } from '@/context/SoundContext';

const TOMMY_MASCOT = "https://static.prod-images.emergentagent.com/jobs/5a9b2831-6b5b-4cfc-b7a9-9566f26820d4/images/8a5b3d0ce503ee6e5d109b6acca877668382c804aa320000e401fda7fbbae6b4.png";

// Custom X (Twitter) icon
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// YouTube icon
const YouTubeIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

export const Footer = () => {
  const { playSound } = useSound();
  
  return (
    <footer 
      className="bg-[#030303] border-t border-teal-500/10 py-16" 
      data-testid="footer"
      role="contentinfo"
      aria-label="Site footer"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4" onClick={() => playSound('pop')}>
              <img src={TOMMY_MASCOT} alt="Tommy" className="w-10 h-10 rounded-full border-2 border-teal-500/30" />
              <span className="font-black text-xl">
                <span className="text-white">Triple</span>
                <span className="bg-gradient-to-r from-teal-400 to-green-400 bg-clip-text text-transparent">TC</span>
              </span>
            </Link>
            <p className="text-zinc-500 text-sm max-w-md leading-relaxed">
              The future of AI work. 10 specialized agents create videos, images, voice, and content on demand. 
              Pay only for results.
            </p>
            <div className="flex gap-4 mt-6" role="list" aria-label="Social media links">
              <a 
                href="https://x.com/tripletc" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-white hover:border-teal-500/50 transition-all"
                aria-label="Follow us on X (Twitter)"
                role="listitem"
                onClick={() => playSound('click')}
              >
                <XIcon className="w-4 h-4" />
              </a>
              <a 
                href="https://youtube.com/@tripletc" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 hover:text-red-500 hover:border-red-500/50 transition-all"
                aria-label="Subscribe on YouTube"
                role="listitem"
                onClick={() => playSound('click')}
              >
                <YouTubeIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-teal-400 font-bold mb-4 text-sm tracking-wide uppercase">Platform</h4>
            <ul className="space-y-3">
              <li><Link to="/agents" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>Browse Agents</Link></li>
              <li><Link to="/my-orders" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>My Orders</Link></li>
              <li><Link to="/developers" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>API Access</Link></li>
              <li><Link to="/subscription" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>Premium</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-teal-400 font-bold mb-4 text-sm tracking-wide uppercase">Support</h4>
            <ul className="space-y-3">
              <li><Link to="/faq" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>FAQ</Link></li>
              <li><Link to="/about" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>About Us</Link></li>
              <li><Link to="/trust" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>Trust Center</Link></li>
              <li><Link to="/terms" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>Privacy Policy</Link></li>
              <li><Link to="/refund" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>Refund Policy</Link></li>
              <li><Link to="/contact" className="text-zinc-400 hover:text-teal-400 text-sm transition-colors" onClick={() => playSound('hover')}>Contact Us</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-zinc-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-zinc-600 text-sm">
                © 2026 TripleTC LLC. All rights reserved.
              </p>
              <p className="text-zinc-700 text-xs mt-1">
                TripleTC™ and the Tommy mascot are trademarks of TripleTC LLC. Patent pending.
              </p>
            </div>
            <div className="flex items-center gap-2 text-zinc-600 text-sm flex-wrap justify-center">
              <span>Payments by</span>
              <span className="text-teal-400 font-semibold">Stripe</span>
              <span className="mx-2 text-zinc-700">•</span>
              <span className="text-white bg-zinc-800 px-2 py-0.5 rounded text-xs">Card</span>
              <span className="text-white bg-gradient-to-r from-blue-500 to-green-500 px-2 py-0.5 rounded text-xs">Google Pay</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
